import { say } from "./words.ts";

/**
 * The lines a page writes to the browser console once when it starts (P1.9-T223), in place of the engine's own
 * banner, which every Phaser game config here switches off with `banner: false`.
 */

/**
 * The console style of the first line: the game's brass, quiet beside the browser's own messages.
 */
export const BANNER_STYLE = "color: #d6b36a";

/**
 * The part of the console a banner writes to.
 */
export interface BannerConsole {
  readonly log: (...args: unknown[]) => void;
}

/**
 * Writes the banner at most once.
 */
export interface Banner {
  /**
   * Writes the banner on the first call and does nothing on later ones.
   *
   * @param build The build's commit as the bundle holds it, or undefined or empty when it was built without one.
   * @param world The world version, for the game's banner.
   */
  readonly print: (build: string | undefined, world: string) => void;
}

/**
 * A commit as a short label.
 *
 * @param commit The commit, or undefined or empty when there is none.
 * @returns Its first seven characters, or the words for a build made without a commit.
 */
export function buildLabel(commit: string | undefined): string {
  return commit === undefined || commit === "" ? say("banner.devBuild") : commit.slice(0, 7);
}

/**
 * The game page's banner: its build and world line in brass, then the line inviting testers.
 *
 * @param out The console.
 * @returns The banner.
 */
export function createGameBanner(out: BannerConsole): Banner {
  let printed = false;
  return {
    print: (build, world) => {
      if (printed) {
        return;
      }
      printed = true;
      out.log(`%c${say("banner.game", { build: buildLabel(build), world })}`, BANNER_STYLE);
      out.log(say("banner.hiring"));
    },
  };
}

/**
 * The editor page's banner: its build line in the same style. The world argument is ignored.
 *
 * @param out The console.
 * @returns The banner.
 */
export function createEditorBanner(out: BannerConsole): Banner {
  let printed = false;
  return {
    print: (build) => {
      if (printed) {
        return;
      }
      printed = true;
      out.log(`%c${say("banner.editor", { build: buildLabel(build) })}`, BANNER_STYLE);
    },
  };
}

/**
 * The game page's banner, written to the page's console when its world has loaded.
 */
export const gameBanner: Banner = createGameBanner(console);

/**
 * The editor page's banner, written to the page's console when the editor starts.
 */
export const editorBanner: Banner = createEditorBanner(console);
